import { useEffect, useRef, useState } from "react";
import { createAppointment, getCustomers, getServices, getStaff } from "../lib/api.ts";

interface CustomerLite {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  visit_count?: number | null;
}

interface Service {
  id: string;
  name: string;
  duration_min: number;
  price_cents: number | null;
  currency: string;
  is_active: number;
}

interface StaffMember {
  id: string;
  name: string;
  is_active: number;
}

interface NewAppointmentModalProps {
  slug: string;
  defaultDate?: string;
  /** "HH:MM" — prefills the time picker. */
  defaultTime?: string;
  /** Pre-selects the staff dropdown to this id. */
  defaultStaffId?: string;
  onClose: () => void;
  onCreated: () => void;
}

function todayStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function NewAppointmentModal({
  slug,
  defaultDate,
  defaultTime,
  defaultStaffId,
  onClose,
  onCreated,
}: NewAppointmentModalProps) {
  const [services, setServices] = useState<Service[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** When set, the last submit hit a slot conflict — show as warning + a
   *  "Book anyway" affordance that retries with allow_overlap: true. */
  const [overlapWarn, setOverlapWarn] = useState(false);

  // Form state
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [serviceSearch, setServiceSearch] = useState("");
  const [serviceMenuOpen, setServiceMenuOpen] = useState(false);
  const [staffId, setStaffId] = useState<string>("");
  const [date, setDate] = useState<string>(defaultDate ?? todayStr());
  const [time, setTime] = useState<string>(defaultTime ?? "10:00");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [isAllDay, setIsAllDay] = useState(false);

  // Existing-customer typeahead. When the operator picks a suggestion, we
  // capture customerId and submit `customer_id` instead of upserting by
  // email — avoids accidentally creating duplicates.
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<CustomerLite[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestTimer = useRef<number | null>(null);

  // Debounced search whenever the customer name changes (and no customerId
  // is locked in from a previous pick).
  useEffect(() => {
    if (customerId) return; // already picked an existing customer
    if (!customerName || customerName.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    if (suggestTimer.current) window.clearTimeout(suggestTimer.current);
    suggestTimer.current = window.setTimeout(() => {
      getCustomers(slug, customerName.trim())
        .then((rows) => {
          setSuggestions(((rows as CustomerLite[]) ?? []).slice(0, 6));
        })
        .catch(() => setSuggestions([]));
    }, 200);
    return () => {
      if (suggestTimer.current) window.clearTimeout(suggestTimer.current);
    };
  }, [customerName, customerId, slug]);

  function pickCustomer(c: CustomerLite) {
    setCustomerId(c.id);
    setCustomerName(c.name);
    setCustomerPhone(c.phone ?? "");
    setCustomerEmail(c.email ?? "");
    setShowSuggestions(false);
  }

  /** Clear the locked customerId when the operator edits any contact field
   *  — they're now describing a different/new person. */
  function unlockCustomer() {
    if (customerId) setCustomerId(null);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [svcResp, stfResp] = await Promise.all([
          getServices(slug),
          getStaff(slug),
        ]);
        if (cancelled) return;
        // Both endpoints return { data, pagination } or raw arrays
        const svcList: Service[] = Array.isArray(svcResp)
          ? svcResp
          : Array.isArray((svcResp as { data?: Service[] }).data)
            ? (svcResp as { data: Service[] }).data
            : [];
        const stfList: StaffMember[] = Array.isArray(stfResp)
          ? stfResp
          : Array.isArray((stfResp as { data?: StaffMember[] }).data)
            ? (stfResp as { data: StaffMember[] }).data
            : [];
        setServices(svcList.filter((s) => s.is_active));
        setStaff(stfList.filter((s) => s.is_active));
        if (svcList.length > 0) setServiceIds([svcList[0].id]);
        if (stfList.length > 0) {
          const preferred = defaultStaffId && stfList.find((s) => s.id === defaultStaffId);
          setStaffId(preferred ? defaultStaffId : stfList[0].id);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, defaultStaffId]);

  const submitBooking = async (allowOverlap: boolean) => {
    setError(null);
    setOverlapWarn(false);

    if (serviceIds.length === 0) {
      setError("Please pick at least one service");
      return;
    }
    if (!staffId) {
      setError("Please pick a staff member");
      return;
    }
    if (!customerName.trim()) {
      setError("Customer name is required");
      return;
    }
    if (!date || !time) {
      setError("Date and time are required");
      return;
    }

    setSubmitting(true);
    try {
      // Backend interprets `${date}T${start_time}:00Z` as UTC. Convert the
      // user's local-time pick into UTC components so the stored ISO matches
      // their wall-clock time when displayed back via toLocaleTimeString.
      // Example: 12:30 PM IST → "2026-05-05T07:00:00Z" stored → 12:30 PM IST
      // displayed.
      let utcDate = date;
      let utcTime = time;
      if (!isAllDay) {
        const local = new Date(`${date}T${time}:00`);
        const iso = local.toISOString(); // "YYYY-MM-DDTHH:MM:SS.sssZ"
        utcDate = iso.slice(0, 10);
        utcTime = iso.slice(11, 16);
      }

      const payload: Record<string, unknown> = {
        service_ids: serviceIds,
        staff_id: staffId,
        date: utcDate,
        // start_time is ignored server-side when is_all_day, but the
        // POST contract still requires HH:mm — send a placeholder.
        start_time: isAllDay ? "00:00" : utcTime,
        is_all_day: isAllDay || undefined,
        allow_overlap: allowOverlap || undefined,
        notes: notes.trim() || undefined,
      };
      if (customerId) {
        payload.customer_id = customerId;
      } else {
        payload.customer = {
          name: customerName.trim(),
          email: customerEmail.trim() || undefined,
          phone: customerPhone.trim() || undefined,
        };
      }
      await createAppointment(slug, payload);
      onCreated();
      onClose();
    } catch (err) {
      const msg = (err as Error).message ?? "Could not book appointment";
      // Treat slot overlap as a soft warning — the operator can choose to
      // book anyway. Other errors stay hard.
      if (/overlap/i.test(msg) && !allowOverlap) {
        setOverlapWarn(true);
      } else {
        setError(msg);
      }
      setSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submitBooking(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={submitting ? undefined : onClose}
      />

      {/* Sheet / dialog */}
      <form
        onSubmit={handleSubmit}
        className="relative w-full md:max-w-md rounded-t-2xl md:rounded-2xl max-h-[92vh] overflow-y-auto"
        style={{
          background: "var(--card-bg)",
          border: "1px solid var(--card-border)",
        }}
      >
        {/* Grab handle (mobile) */}
        <div className="flex justify-center pt-3 pb-2 md:hidden">
          <div
            className="w-10 h-1 rounded-full"
            style={{ background: "var(--border)" }}
          />
        </div>

        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <h2 style={{ fontSize: "1.05rem", fontWeight: 600, color: "var(--text)" }}>
            New appointment
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-lg"
            style={{ color: "var(--text-muted)" }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-3">
          {loadingOptions ? (
            <div style={{ color: "var(--text-muted)", fontSize: "0.9rem", padding: "1rem 0" }}>
              Loading…
            </div>
          ) : (
            <>
              <div style={{ position: "relative" }}>
                <label className="label">
                  Service
                  {serviceIds.length > 1 && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: "var(--ink-faint)" }}>
                      · {serviceIds.length} selected · total{" "}
                      {services
                        .filter((s) => serviceIds.includes(s.id))
                        .reduce((acc, s) => acc + s.duration_min, 0)}{" "}
                      min
                    </span>
                  )}
                </label>

                {/* Selected-service chips */}
                {serviceIds.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                    {serviceIds.map((id) => {
                      const svc = services.find((s) => s.id === id);
                      if (!svc) return null;
                      return (
                        <span
                          key={id}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 8px",
                            borderRadius: 999,
                            background: "var(--accent-soft)",
                            color: "var(--accent-ink)",
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                        >
                          {svc.name} · {svc.duration_min}m
                          <button
                            type="button"
                            onClick={() => setServiceIds((ids) => ids.filter((x) => x !== id))}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "var(--accent-ink)",
                              cursor: "pointer",
                              padding: 0,
                              fontSize: 14,
                              lineHeight: 1,
                            }}
                            aria-label={`Remove ${svc.name}`}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                <input
                  className="input"
                  value={serviceSearch}
                  onChange={(e) => {
                    setServiceSearch(e.target.value);
                    setServiceMenuOpen(true);
                  }}
                  onFocus={() => setServiceMenuOpen(true)}
                  onBlur={() => window.setTimeout(() => setServiceMenuOpen(false), 150)}
                  placeholder={serviceIds.length === 0 ? "Search services…" : "Add another service…"}
                  autoComplete="off"
                />

                {serviceMenuOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 2px)",
                      left: 0,
                      right: 0,
                      background: "var(--paper-raised)",
                      border: "1px solid var(--rule)",
                      borderRadius: 8,
                      boxShadow: "var(--shadow-md)",
                      padding: 4,
                      zIndex: 10,
                      maxHeight: 220,
                      overflowY: "auto",
                    }}
                  >
                    {(() => {
                      const q = serviceSearch.trim().toLowerCase();
                      const matches = services
                        .filter((s) => !serviceIds.includes(s.id))
                        .filter((s) => !q || s.name.toLowerCase().includes(q));
                      if (matches.length === 0) {
                        return (
                          <div style={{ padding: "8px 10px", fontSize: 12.5, color: "var(--ink-faint)" }}>
                            {services.length === 0 ? "No services configured." : "No more matches."}
                          </div>
                        );
                      }
                      return matches.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setServiceIds((ids) => [...ids, s.id]);
                            setServiceSearch("");
                          }}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            textAlign: "left",
                            padding: "8px 10px",
                            background: "transparent",
                            border: "none",
                            borderRadius: 6,
                            fontSize: 13,
                            color: "var(--ink)",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "var(--paper-well)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                          }}
                        >
                          <span>{s.name}</span>
                          <span style={{ color: "var(--ink-faint)", fontSize: 12 }}>{s.duration_min} min</span>
                        </button>
                      ));
                    })()}
                  </div>
                )}
              </div>

              <div>
                <label className="label">Staff</label>
                <select
                  className="input"
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                >
                  {staff.length === 0 && <option value="">— no staff —</option>}
                  {staff.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2">
                <div style={{ flex: 1 }}>
                  <label className="label">Date</label>
                  <input
                    type="date"
                    className="input"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
                {!isAllDay && (
                  <div style={{ flex: 1 }}>
                    <label className="label">Time</label>
                    <input
                      type="time"
                      className="input"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  id="is_all_day"
                  type="checkbox"
                  checked={isAllDay}
                  onChange={(e) => setIsAllDay(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: "var(--ink)" }}
                />
                <label htmlFor="is_all_day" style={{ fontSize: 14, color: "var(--ink)" }}>
                  All-day event (no specific start time)
                </label>
              </div>

              <div
                style={{
                  height: "1px",
                  background: "var(--border)",
                  margin: "0.25rem 0",
                }}
              />

              <div style={{ position: "relative" }}>
                <label className="label">
                  Customer name
                  {customerId && (
                    <button
                      type="button"
                      onClick={() => {
                        setCustomerId(null);
                        setCustomerName("");
                        setCustomerPhone("");
                        setCustomerEmail("");
                      }}
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        color: "var(--accent-ink)",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      linked to existing customer · clear
                    </button>
                  )}
                </label>
                <input
                  className="input"
                  value={customerName}
                  onChange={(e) => {
                    setCustomerName(e.target.value);
                    if (customerId) unlockCustomer();
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => {
                    // Delay so click on a suggestion can fire first.
                    window.setTimeout(() => setShowSuggestions(false), 150);
                  }}
                  placeholder="Jordan Taylor"
                  autoComplete="off"
                />
                {showSuggestions && !customerId && suggestions.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 2px)",
                      left: 0,
                      right: 0,
                      background: "var(--paper-raised)",
                      border: "1px solid var(--rule)",
                      borderRadius: 8,
                      boxShadow: "var(--shadow-md)",
                      padding: 4,
                      zIndex: 10,
                      maxHeight: 220,
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontSize: 10,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "var(--ink-faint)",
                        padding: "6px 10px 4px",
                      }}
                    >
                      Existing customers
                    </div>
                    {suggestions.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault(); // don't blur the input
                          pickCustomer(c);
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                          padding: "8px 10px",
                          background: "transparent",
                          border: "none",
                          borderRadius: 6,
                          fontSize: 13,
                          color: "var(--ink)",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "var(--paper-well)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 2 }}>
                          {[c.phone, c.email].filter(Boolean).join(" · ") || "no contact info"}
                          {typeof c.visit_count === "number" && c.visit_count > 0 && (
                            <span style={{ marginLeft: 8 }}>· {c.visit_count} visits</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <div style={{ flex: 1 }}>
                  <label className="label">Phone</label>
                  <input
                    className="input"
                    value={customerPhone}
                    onChange={(e) => {
                      setCustomerPhone(e.target.value);
                      unlockCustomer();
                    }}
                    placeholder="Optional"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="label">Email</label>
                  <input
                    type="email"
                    className="input"
                    value={customerEmail}
                    onChange={(e) => {
                      setCustomerEmail(e.target.value);
                      unlockCustomer();
                    }}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div>
                <label className="label">Notes</label>
                <textarea
                  className="input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Optional"
                />
              </div>

              {error && (
                <div className="alert-error" style={{ marginTop: "0.25rem" }}>
                  {error}
                </div>
              )}

              {overlapWarn && (
                <div
                  style={{
                    marginTop: "0.25rem",
                    padding: "10px 12px",
                    borderRadius: 8,
                    background: "color-mix(in oklab, var(--status-noshow) 8%, transparent)",
                    border: "1px solid color-mix(in oklab, var(--status-noshow) 40%, transparent)",
                    color: "var(--ink)",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <strong>Slot overlaps an existing booking.</strong>{" "}
                    The staff member already has another appointment in this time range.
                    You can still book if it's intentional (walk-in, double-up, etc.).
                  </div>
                  <button
                    type="button"
                    onClick={() => void submitBooking(true)}
                    disabled={submitting}
                    className="btn-primary"
                    style={{ fontSize: 12.5, padding: "6px 12px" }}
                  >
                    {submitting ? "Booking…" : "Book anyway"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex gap-2 px-5 py-3"
          style={{ borderTop: "1px solid var(--border)" }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn-secondary"
            style={{ flex: 1 }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || loadingOptions}
            className="btn-primary"
            style={{ flex: 1 }}
          >
            {submitting ? "Booking…" : "Book"}
          </button>
        </div>
      </form>
    </div>
  );
}
